import React, { FC, useContext } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { Button, Form } from "@natera/form";
import {
  DrawRequestContext,
  HeapAnalyticDataContext,
  OrderRenewalContext,
} from "@app/provider";
import { HEAP_EVENTS } from "@app/provider/types";
import { AppointmentAddress } from "@app/components/appointmentAddress";
import { AppointmentTimeslots } from "@app/components/appointmentTimeslots";

import { StepperContent } from "@app/components/ui/layout";
import { PatientPortalCtaBanner, RenewalBanner } from "@app/components/banners";
import { routes } from "@app/routing";
import { useQuery } from "@app/utils";

import "./appointmentTimeConfirmationStep.scss";

const messages = defineMessages({
  appointmentTimeConfirmationTitle: {
    id: "appointmentTimeConfirmationTitle",
    defaultMessage: "Mobile Blood Draw",
  },
  appointmentTimeConfirmationDescriptionContent: {
    id: "appointmentTimeConfirmationDescriptionContent",
    defaultMessage:
      "You will get a call from the scheduling team to confirm your appointment 24-48 hours prior to your first selected time slot.",
  },
  appointmentTimeConfirmationModifyAppointment: {
    id: "appointmentTimeConfirmationModifyAppointment",
    defaultMessage: "Change Appointment",
  },
  appointmentTimeConfirmationBackHome: {
    id: "appointmentTimeConfirmationBackHome",
    defaultMessage: "Home",
  },
});

interface Props {
  handleModifyAppointment: () => void;
  onBack?: () => void;
  isPublicRequest?: boolean;
  token?: string;
}

const AppointmentTimeConfirmationStep: FC<Props> = ({
  handleModifyAppointment,
  onBack,
  isPublicRequest,
  token: tokenProp,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const query = useQuery();
  const {
    drawRequestData: drawRequest,
    selectedTimeZone,
    verifiedSampleDrawData,
  } = useContext(DrawRequestContext);
  const { renewalInfo, openRenewalDialog } =
    React.useContext(OrderRenewalContext);
  const { drawRequestDataForHeapEventData } = React.useContext(
    HeapAnalyticDataContext
  );
  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    handleModifyAppointment();
  };

  // UPP-5865/5890: the MP guest-flow verify minted a DOB-proof handle. Offer
  // account creation, carrying the same MP token + handle (`si`) so the sign-up
  // landing skips the DOB screen; the server gates still validate the handle, so
  // a stale/expired one falls back to DOB. Public MP has no URL `token`, so fall
  // back to the token prop (the order-match token threaded down the stepper).
  const token = tokenProp ?? query.get("token") ?? undefined;
  const intentToken = verifiedSampleDrawData?.intentToken;
  const canCreateAccount = !!intentToken && !!token;

  const handleCreateAccount = () => {
    heap.track(HEAP_EVENTS.upp_mp_click_create_account, {
      business_unit: drawRequestDataForHeapEventData?.business_unit,
      test_name: drawRequestDataForHeapEventData?.test_name,
      lims_clinic_id: drawRequestDataForHeapEventData?.lims_clinic_id,
      flow: isPublicRequest ? "public_blood_draw" : "guest_blood_draw",
    });
    navigate(`${routes.signUp}?token=${token}&si=${intentToken}`);
  };

  const renderRenewalBanner = () =>
    renewalInfo?.hasRenewal &&
    renewalInfo?.isLastSignateraOrder && (
      <RenewalBanner
        isMobilePhlebotomyPage
        openRenewalDialog={openRenewalDialog}
        limsId={renewalInfo?.clinicLimsId}
        orderUidPublic={renewalInfo?.renewalOrderUidPublic}
      />
    );

  return (
    <div className="stepper__container">
      <StepperContent
        title={intl.formatMessage(messages.appointmentTimeConfirmationTitle)}
        subTitle={intl.formatMessage(
          messages.appointmentTimeConfirmationDescriptionContent
        )}
      >
        <Form
          onSubmit={handleSubmit}
          noValidate
          buttons={
            <>
              {!isPublicRequest && (
                <Button type="submit" outlined data-testid="change-appointment">
                  {intl.formatMessage(
                    messages.appointmentTimeConfirmationModifyAppointment
                  )}
                </Button>
              )}
              {onBack && (
                <Button
                  unelevated={!canCreateAccount}
                  outlined={canCreateAccount}
                  onClick={onBack}
                  type="button"
                  data-testid="back-to-home"
                >
                  {intl.formatMessage(
                    messages.appointmentTimeConfirmationBackHome
                  )}
                </Button>
              )}
              {/* UPP-5865/5890: CTA below the actions, porting a guest into
                  tokenized sign-up (token + `si`) via the GIS portal hero. */}
              {canCreateAccount && (
                <PatientPortalCtaBanner onClick={handleCreateAccount} />
              )}
            </>
          }
        >
          <div className="content">
            {drawRequest && (
              <AppointmentTimeslots
                drawRequest={drawRequest}
                selectedTimeZone={selectedTimeZone}
                isConfirmationStep
              />
            )}
            {drawRequest && (
              <AppointmentAddress
                shippingAddress={drawRequest.shippingAddress}
              />
            )}
            {renderRenewalBanner()}
          </div>
        </Form>
      </StepperContent>
    </div>
  );
};
export default AppointmentTimeConfirmationStep;
